import { AdditiveBlending, BufferAttribute, BufferGeometry, Color, DoubleSide, NormalBlending, ShaderMaterial, Vector2, Vector3, Vector4 } from 'three';
import type { IUniform, Texture } from 'three';
import { ATMOSPHERE, LIGHTING, NOISE, WORLD_UNIFORMS } from '../shaders/chunks';
import type { SharedUniforms } from '../shaders/shared';

/**
 * Ground height from the toroidal 1 m heightfield (R height, G coverage). Used to plant ambient
 * particles on the terrain and to fade every particle softly where it meets the ground, so
 * nothing ever cuts a hard line into the sand or turf ("soft particles" without a depth read).
 */
const GROUND = /* glsl */ `
uniform sampler2D tHeight;
uniform float uHeightSize;
vec2 groundAt(vec2 p) {
  vec2 c = floor(p);
  vec2 f = p - c;
  vec2 a = texelFetch(tHeight, ivec2(mod(c, uHeightSize)), 0).rg;
  vec2 b = texelFetch(tHeight, ivec2(mod(c + vec2(1.0, 0.0), uHeightSize)), 0).rg;
  vec2 d = texelFetch(tHeight, ivec2(mod(c + vec2(0.0, 1.0), uHeightSize)), 0).rg;
  vec2 e = texelFetch(tHeight, ivec2(mod(c + vec2(1.0, 1.0), uHeightSize)), 0).rg;
  return mix(mix(a, b, f.x), mix(d, e, f.x), f.y);
}
// 0 at/below the ground → 1 at \`soft\` metres above it (no fade where there is no ground data).
float groundFade(vec3 wp, float soft) {
  vec2 g = groundAt(wp.xz);
  return g.y < 0.5 ? 1.0 : smoothstep(0.0, soft, wp.y - g.x);
}
`;

/**
 * Burst particles: spawned on the CPU into a ring buffer, simulated analytically on the GPU
 * (p = p0 + v0·t + ½g·t²) so there is no per-frame upload. Types:
 * 0 = soft puff, 1 = sparkle, 2 = petal/leaf chip, 3 = sand grain, 4 = streak.
 */
const BURST_VS = /* glsl */ `
${WORLD_UNIFORMS}
attribute vec4 aP0;     // pos, spawn time
attribute vec4 aV0;     // vel, life
attribute vec4 aParams; // size0, size1, type, rand
attribute vec4 aColor;  // rgb, alpha
varying vec2 vUv;
varying vec4 vColor;
varying float vType;
varying float vAge;
varying float vRand;
varying vec3 vWorldPos;
void main() {
  float age = uTime - aP0.w;
  float life = aV0.w;
  float t01 = age / life;
  if (age < 0.0 || t01 > 1.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  float type = aParams.z;
  float grav = type < 0.5 ? 1.5 : (type < 1.5 ? -2.0 : (type < 2.5 ? -3.5 : (type < 3.5 ? -14.0 : (type > 4.5 ? -0.15 : 0.0))));
  float drag = type < 0.5 || type > 4.5 ? 1.8 : 0.6;
  float k = (1.0 - exp(-drag * age)) / drag;
  vec3 p = aP0.xyz + aV0.xyz * k + vec3(0.0, grav * 0.5 * age * age, 0.0);
  if (type > 1.5 && type < 2.5) p.xz += uWind.xy * age * 2.0 + vec2(sin(age * 6.0 + aParams.w * 20.0), cos(age * 5.0 + aParams.w * 13.0)) * 0.25;
  float size = mix(aParams.x, aParams.y, t01);
  vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 camUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  vec2 corner = position.xy;
  if (type > 3.5 && type < 4.5) {
    // streaks stretch along their velocity
    vec3 vdir = normalize(aV0.xyz + 1e-4);
    vec3 side = normalize(cross(vdir, normalize(cameraPosition - p)));
    p += vdir * corner.y * size * 4.0 + side * corner.x * size * 0.25;
  } else {
    float a = aParams.w * 6.28 + age * (type > 1.5 && type < 2.5 ? 4.0 : 0.6);
    corner = vec2(corner.x * cos(a) - corner.y * sin(a), corner.x * sin(a) + corner.y * cos(a));
    p += (camRight * corner.x + camUp * corner.y) * size;
  }
  vUv = position.xy * 0.5 + 0.5;
  vColor = aColor;
  vType = type;
  vAge = t01;
  vRand = aParams.w;
  vWorldPos = p;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}
`;

const BURST_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
${GROUND}
varying vec2 vUv;
varying vec4 vColor;
varying float vType;
varying float vAge;
varying float vRand;
varying vec3 vWorldPos;
void main() {
  vec2 q = vUv * 2.0 - 1.0;
  float r = length(q);
  float a;
  vec3 col = vColor.rgb;
  if (vType < 0.5) {
    // Painterly puff: lumpy disc.
    float edge = 0.8 + 0.2 * vnoise(q * 3.0 + vRand * 10.0);
    a = smoothstep(edge, edge * 0.55, r) * (1.0 - vAge) * (1.0 - vAge);
    col *= mix(0.82, 1.08, 1.0 - r);
  } else if (vType < 1.5) {
    float star = max(1.0 - abs(q.x * q.y) * 30.0, 0.0) * (1.0 - r);
    a = (smoothstep(1.0, 0.0, r) * 0.35 + star) * (1.0 - vAge);
  } else if (vType < 2.5) {
    float leaf = abs(q.x) / max(1.0 - q.y * q.y, 0.05);
    a = step(leaf, 0.55) * step(r, 1.0) * (1.0 - smoothstep(0.7, 1.0, vAge));
  } else if (vType < 3.5) {
    a = smoothstep(1.0, 0.2, r) * (1.0 - vAge);
  } else if (vType > 4.5) {
    // Dust: a soft, wispy gaussian that thins as it spreads.
    float wisp = 0.65 + 0.35 * vnoise(q * 2.5 + vRand * 17.0 + vAge * 1.5);
    a = exp(-r * r * 3.2) * wisp * smoothstep(0.0, 0.15, vAge) * (1.0 - vAge) * (1.0 - vAge);
  } else {
    a = smoothstep(1.0, 0.0, abs(q.x)) * smoothstep(1.0, 0.3, abs(q.y)) * (1.0 - vAge);
  }
  a *= vColor.a;
  // Soft where it meets the ground: puffs and dust melt into the sand instead of clipping.
  a *= groundFade(vWorldPos, vType < 0.5 || vType > 4.5 ? 0.45 : 0.06);
  // …and never balloon into blurry orbs right in front of the lens.
  a *= smoothstep(0.8, 3.5, distance(vWorldPos, cameraPosition));
  if (a < 0.01) discard;
  #ifdef ADDITIVE
    gl_FragColor = vec4(col * a, 1.0);
  #else
    // Lit like the world (sun + sky), so a white puff reads as a sunlit cloud, not grey card.
    col *= uSunColor * 0.5 + hemiAmbient(vec3(0.0, 1.0, 0.0)) * 1.1;
    gl_FragColor = vec4(applyFog(col, vWorldPos), a);
  #endif
}
`;

export function createBurstMaterial(shared: SharedUniforms, additive: boolean, heightfield: Texture): ShaderMaterial {
  return new ShaderMaterial({
    name: additive ? 'burst-add' : 'burst',
    uniforms: { ...shared.all(), tHeight: { value: heightfield }, uHeightSize: { value: (heightfield.image as { width: number }).width } },
    vertexShader: BURST_VS,
    fragmentShader: BURST_FS,
    defines: additive ? { ADDITIVE: 1 } : {},
    transparent: true,
    depthWrite: false,
    blending: additive ? AdditiveBlending : NormalBlending,
  });
}

/**
 * Ambient particles: a box of drifting pollen / blowing sand that wraps around the camera.
 * Zero CPU cost — positions are a pure function of (seed, time, camera).
 */
const AMBIENT_VS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
attribute vec4 aSeed;
uniform vec3 uBoxCenter;
uniform vec3 uBoxSize;
uniform float uKind;     // 0 pollen, 1 sand
uniform float uSize;
${GROUND}
varying vec2 vUv;
varying float vAlpha;
varying float vTw;
varying float vGust;
varying float vVeil;
varying vec3 vWorldPos;
void main() {
  bool sand = uKind > 0.5;
  // Most sand grains saltate: short hops just above the surface. A few become dust high up.
  float dusty = step(0.86, aSeed.w);
  // Veils: soft, stretched sheets of blowing sand skimming low and fast over the surface.
  float veil = sand ? step(0.7, aSeed.z) * (1.0 - dusty) : 0.0;
  float spd = sand ? mix(mix(7.0, 13.0, aSeed.w), mix(12.0, 19.0, aSeed.w), veil) * (1.0 - dusty * 0.4) : 1.4 * (0.6 + aSeed.w * 0.8);
  vec3 wind = vec3(uWind.x, 0.0, uWind.y) * spd * uWind.z;
  vec3 p = aSeed.xyz * uBoxSize + wind * uTime;
  if (!sand) p.x += sin(uTime * (0.5 + aSeed.w) + aSeed.y * 20.0) * 1.5;
  vec3 lo = uBoxCenter - uBoxSize * 0.5;
  p.xz = mod(p.xz - lo.xz, uBoxSize.xz) + lo.xz;
  vec2 g = groundAt(p.xz);
  float hop;
  if (sand) {
    float ph = fract(uTime * (0.9 + aSeed.y * 1.4) + aSeed.z * 7.0);
    float hh = mix(0.04 + aSeed.x * aSeed.x * 0.5, 1.5 + aSeed.x * 4.0, dusty);
    hop = hh * (dusty > 0.5 ? (0.5 + 0.5 * sin(uTime * 0.6 + aSeed.x * 40.0)) : 4.0 * ph * (1.0 - ph));
    hop = mix(hop, 0.06 + aSeed.x * 0.32, veil);
  } else {
    hop = 0.4 + aSeed.y * aSeed.y * 9.0 + sin(uTime * 0.7 + aSeed.w * 30.0) * 0.8;
  }
  if (sand) {
    // Spindrift: grains blown off a crest keep their height as the ground falls away beneath
    // them, then settle gently downwind — a plume streaming with the wind, never a curtain.
    vec2 wd2 = normalize(uWind.xy + 1e-4);
    float carried = g.x + hop;
    for (int k = 1; k <= 4; k++) {
      float s = float(k) * 1.6;
      carried = max(carried, groundAt(p.xz - wd2 * s).x + hop * (1.0 - dusty) - s * 0.22);
    }
    hop = carried - g.x;
    // Blowing sand comes in gusty waves rather than an even drizzle.
    float gust = vnoise(p.xz * 0.03 - uWind.xy * uTime * 0.35);
    vGust = smoothstep(0.3, 0.7, gust) * 0.85 + 0.15;
  } else {
    vGust = 1.0;
  }
  p.y = g.x + hop;
  vec2 rel = (p.xz - uBoxCenter.xz) / (uBoxSize.xz * 0.5);
  float edgeFade = 1.0 - smoothstep(0.7, 1.0, max(abs(rel.x), abs(rel.y)));
  edgeFade *= smoothstep(0.3, 0.7, g.y);
  float dist = distance(p, cameraPosition);
  float near = smoothstep(1.5, 5.0, dist) * (1.0 - smoothstep(45.0, 70.0, dist));
  vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 camUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  float size = mix(uSize * (0.6 + aSeed.w * 0.8) * (1.0 + dusty * 1.5), 0.28 + aSeed.y * 0.4, veil);
  vec3 offs;
  if (sand) {
    // Streaks along the wind relative to the pearl's motion.
    // Blowing sand is a sheet lying on the dune: its long axis follows the world wind (fixed —
    // it never swings with the camera or with the pearl's heading) and both axes are tilted into
    // the ground's own slope, so it hugs the surface instead of jutting out of a dune face.
    // Grains carried high off a crest relax back to level.
    vec2 wdir = normalize(uWind.xy + vec2(1e-4, 0.0));
    vec3 d = vec3(wdir.x, 0.0, wdir.y);
    vec3 side = vec3(-wdir.y, 0.0, wdir.x);
    float hug = 1.0 - smoothstep(0.3, 1.5, hop);
    float sd = (groundAt(p.xz + wdir).x - groundAt(p.xz - wdir).x) * 0.5;
    float ss = (groundAt(p.xz + side.xz).x - groundAt(p.xz - side.xz).x) * 0.5;
    d = normalize(d + vec3(0.0, sd * hug, 0.0));
    side = normalize(side + vec3(0.0, ss * hug, 0.0));
    float stretch = 1.0 + uWind.z * 1.6 + veil * 2.2;
    offs = d * position.y * size * stretch + side * position.x * size * mix(0.5, 0.9, veil);
  } else {
    offs = (camRight * position.x + camUp * position.y) * size;
  }
  vUv = position.xy * 0.5 + 0.5;
  vAlpha = edgeFade * near * vGust;
  vTw = 0.5 + 0.5 * sin(uTime * (3.0 + aSeed.w * 9.0) + aSeed.x * 50.0);
  vVeil = veil;
  vWorldPos = p + offs;
  gl_Position = projectionMatrix * viewMatrix * vec4(p + offs, 1.0);
}
`;

const AMBIENT_FS = /* glsl */ `
${GROUND}
uniform vec3 uColor;
uniform vec3 uGlint;
uniform float uKind;
uniform float uOpacity;
varying vec2 vUv;
varying float vAlpha;
varying float vTw;
varying float vVeil;
varying vec3 vWorldPos;
void main() {
  vec2 q = vUv * 2.0 - 1.0;
  float r = length(q);
  float a = smoothstep(1.0, 0.0, r) * vAlpha * uOpacity;
  vec3 col = uColor;
  if (vVeil > 0.5) {
    // A wisp of blowing sand: soft, long, translucent.
    a = exp(-(q.x * q.x * 2.6 + q.y * q.y * 1.3)) * vAlpha * uOpacity * 0.2;
    col = uColor * 0.85;
  } else if (uKind > 0.5) {
    col = mix(uColor, uGlint * 5.0, pow(vTw, 5.0));
    a *= 0.5 + vTw * 0.5;
  }
  // Melt into the ground rather than clip through it.
  a *= groundFade(vWorldPos, uKind > 0.5 ? mix(0.04, 0.12, vVeil) : 0.3);
  if (a < 0.004) discard;
  gl_FragColor = vec4(col * a, 1.0);
}
`;

export function createAmbientMaterial(shared: SharedUniforms, kind: 'pollen' | 'sand', color: Vector3, glint: Vector3, heightfield: Texture): ShaderMaterial {
  const u: Record<string, IUniform> = {
    ...shared.all(),
    tHeight: { value: heightfield },
    uHeightSize: { value: (heightfield.image as { width: number }).width },
    uBoxCenter: { value: new Vector3() },
    uBoxSize: { value: kind === 'sand' ? new Vector3(90, 14, 110) : new Vector3(80, 30, 110) },
    uKind: { value: kind === 'sand' ? 1 : 0 },
    uSize: { value: kind === 'sand' ? 0.03 : 0.035 },
    uColor: { value: color },
    uGlint: { value: glint },
    uOpacity: { value: kind === 'sand' ? 0.55 : 0.5 },
  };
  return new ShaderMaterial({
    name: `ambient-${kind}`,
    uniforms: u,
    vertexShader: AMBIENT_VS,
    fragmentShader: AMBIENT_FS,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
  });
}

// ------------------------------------------------------------------------------------------ butterflies

/**
 * Butterflies: two hinged wing quads per instance, animated entirely on the GPU. Each one loops
 * lazily around a home point that drifts downwind, bobs over the grass (heightfield), flaps with
 * the odd glide, and faces where it's going. Homes wrap around a box near the camera.
 */
const BUTTERFLY_VS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${GROUND}
attribute vec4 aSeed;
uniform vec3 uBoxCenter;
uniform vec3 uBoxSize;
// Keep-out capsule (menu): segment a → b in xz, radius, strength 0..1.
uniform vec4 uAvoidSeg;
uniform vec2 uAvoid;
varying vec2 vUv;
varying float vKind;
varying float vFade;
varying vec3 vWorldPos;
// Slide a flight path out of the keep-out capsule: everything inside is mapped smoothly onto
// [R, 1.5R] and the band out to 2R makes room, so butterflies glide round it (no popping).
vec2 avoid(vec2 p) {
  if (uAvoid.y <= 0.0) return p;
  vec2 a = uAvoidSeg.xy;
  vec2 ab = uAvoidSeg.zw - a;
  float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 1e-4), 0.0, 1.0);
  vec2 c = a + ab * t;
  vec2 d = p - c;
  float l = length(d);
  float R = uAvoid.x;
  if (l >= 2.0 * R) return p;
  vec2 dir = l > 1e-3 ? d / l : vec2(1.0, 0.0);
  float l2 = l < R ? R + l * 0.5 : l + 0.5 * R * (1.0 - smoothstep(R, 2.0 * R, l));
  return c + dir * mix(l, l2, uAvoid.y);
}
void main() {
  float t = uTime;
  float ph = aSeed.w * 6.2831853;
  float sp = 0.6 + aSeed.w * 0.5;
  vec3 home = aSeed.xyz * uBoxSize + vec3(uWind.x, 0.0, uWind.y) * t * 0.5;
  vec3 lo = uBoxCenter - uBoxSize * 0.5;
  home.xz = mod(home.xz - lo.xz, uBoxSize.xz) + lo.xz;
  float a1 = t * 0.37 * sp + ph;
  float a2 = t * 0.91 * sp + ph * 2.0;
  float a3 = t * 0.29 * sp + ph;
  float a4 = t * 0.73 * sp + ph * 3.0;
  vec2 xz = avoid(home.xz + vec2(sin(a1) * 2.4 + sin(a2) * 0.8, cos(a3) * 2.1 + sin(a4) * 0.9));
  vec2 vel = vec2(cos(a1) * 2.4 * 0.37 * sp + cos(a2) * 0.8 * 0.91 * sp, -sin(a3) * 2.1 * 0.29 * sp + cos(a4) * 0.9 * 0.73 * sp);
  vec2 g = groundAt(xz);
  float y = g.x + 0.6 + 0.9 * (0.5 + 0.5 * sin(t * 0.8 * sp + ph * 1.7)) + 0.1 * sin(t * 7.0 + ph);
  // Flap fast; every so often hold the wings up and glide.
  float glide = smoothstep(0.65, 0.92, sin(t * 0.9 + ph * 2.3));
  float flap = mix(sin(t * (13.0 + aSeed.w * 6.0) + ph) * 1.1, 0.4, glide);
  float side = position.z;
  float wx = position.x;
  float wy = position.y;
  float span = 0.13 * (0.8 + aSeed.y * 0.5);
  vec3 local = vec3(side * wx * cos(flap) * span, wx * sin(flap) * span, wy * span * 1.25);
  float yaw = atan(vel.x, vel.y);
  float cy = cos(yaw);
  float sy = sin(yaw);
  vec3 wpos = vec3(xz.x, y, xz.y) + vec3(local.x * cy + local.z * sy, local.y, -local.x * sy + local.z * cy);
  float dist = distance(wpos, cameraPosition);
  vFade = (1.0 - smoothstep(22.0, 38.0, dist)) * smoothstep(0.6, 1.6, dist) * step(0.6, g.y);
  vUv = vec2(wx, wy + 0.5);
  vKind = floor(aSeed.y * 3.999);
  vWorldPos = wpos;
  gl_Position = projectionMatrix * viewMatrix * vec4(wpos, 1.0);
}
`;

const BUTTERFLY_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
uniform vec3 uWingColors[4];
varying vec2 vUv;
varying float vKind;
varying float vFade;
varying vec3 vWorldPos;
void main() {
  if (vFade < ign(gl_FragCoord.xy)) discard;
  // Silhouette: a rounded forewing and a smaller hindwing.
  vec2 u = vUv;
  float fore = length((u - vec2(0.52, 0.66)) * vec2(1.2, 1.55));
  float hind = length((u - vec2(0.4, 0.28)) * vec2(1.5, 1.85));
  float m = min(fore, hind);
  if (m > 0.55) discard;
  int k = int(vKind + 0.5);
  vec3 col = uWingColors[0];
  if (k == 1) col = uWingColors[1];
  else if (k == 2) col = uWingColors[2];
  else if (k == 3) col = uWingColors[3];
  col = mix(col, col * 0.22, smoothstep(0.4, 0.53, m) * 0.75);
  col = mix(col, vec3(0.92), smoothstep(0.1, 0.05, length(u - vec2(0.62, 0.74))) * 0.55);
  col *= mix(0.35, 1.0, smoothstep(0.02, 0.12, u.x));
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 c = col * (uSunColor * 0.42 + hemiAmbient(vec3(0.0, 1.0, 0.0)) * 0.9);
  c += col * uSunColor * pow(max(dot(-V, uSunDir), 0.0), 3.0) * 0.3;
  gl_FragColor = vec4(applyFog(c, vWorldPos), 1.0);
}
`;

/** Two wing quads hinged on the body axis: x outward 0..1, y along the body −½..½, z = side (±1). */
export function butterflyGeometry(): BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  for (const side of [1, -1]) {
    const b = pos.length / 3;
    pos.push(0, -0.5, side, 1, -0.5, side, 1, 0.5, side, 0, 0.5, side);
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setIndex(idx);
  return g;
}

export function createButterflyMaterial(shared: SharedUniforms, heightfield: Texture, colors: string[]): ShaderMaterial {
  return new ShaderMaterial({
    name: 'butterflies',
    uniforms: {
      ...shared.all(),
      tHeight: { value: heightfield },
      uHeightSize: { value: (heightfield.image as { width: number }).width },
      uBoxCenter: { value: new Vector3() },
      uBoxSize: { value: new Vector3(40, 6, 46) },
      uAvoidSeg: { value: new Vector4() },
      uAvoid: { value: new Vector2(0, 0) },
      uWingColors: { value: colors.map((c) => new Color(c).multiplyScalar(0.8)) },
    },
    vertexShader: BUTTERFLY_VS,
    fragmentShader: BUTTERFLY_FS,
    side: DoubleSide,
  });
}
